import Utilities from '@/Utility/Utilities';
import LocatorHelper from '@/Utility/LocatorHelper';

export default class KendoDropDownHelper {

    private static canvas: HTMLCanvasElement;

    public static assignWidgetPopupLocators(widget, parentLocator, itemLocatorStrategy?) {
        if (Utilities.isNullOrUndefined(parentLocator)) {
            return;
        }

        if (Utilities.isFunction(itemLocatorStrategy)) {
            if (itemLocatorStrategy["isEmpty"] === true) { 
                itemLocatorStrategy = KendoDropDownHelper.defaultItemLocatorStrategy;
            }
        } else {
            itemLocatorStrategy = KendoDropDownHelper.defaultItemLocatorStrategy;
        }

        let offset = 0;
        let popupElement = widget.popup.element;

        let $emptyEntry = popupElement.find('.k-list-optionlabel'); // Leeres Element z.B. im Filter
        if ($emptyEntry.length === 1) {
            offset++;
            KendoDropDownHelper.applyBindings($emptyEntry, {
                autoLocator: LocatorHelper.buildLocator(parentLocator, KendoDropDownHelper.emptyItemLocator)
            });
        }

        popupElement.find('.k-item[role=option]').each((id: number, el: HTMLElement) => {
            const $el = $(el);
            const idx = +$el.data('offset-index');
            if (isNaN(idx) || idx < 0)
                return;

            const item = widget.dataItem(offset + idx);

            let loc = itemLocatorStrategy(item, idx, $el);

            if (Utilities.isNullOrUndefined(loc)) {
                loc = idx;
            }

            KendoDropDownHelper.applyBindings($el, {
                autoLocator: LocatorHelper.buildLocator(parentLocator, loc.toString())
            });
        });
    }

    public static bindWidgetPopupLocators(widget, parentLocator, itemLocatorStrategy?) {
        if (Utilities.isNullOrUndefined(parentLocator)) {
            return;
        }

        widget.bind('dataBound', () => {
            KendoDropDownHelper.assignWidgetPopupLocators(widget, parentLocator, itemLocatorStrategy);
        });
    }

    public static assignWidgetValueLocator(widget, parentLocator) {
        if (Utilities.isNullOrUndefined(parentLocator)) {
            return;
        }

        widget.wrapper.find('*').addBack().removeAttr('data-locator');
        KendoDropDownHelper.applyBindings(widget.wrapper, {
            autoLocator: parentLocator
        });
    }

    public static bindWidgetValueLocator(widget, parentLocator) {
        if (Utilities.isNullOrUndefined(parentLocator)) {
            return;
        }

        let assign = () => {
            KendoDropDownHelper.assignWidgetValueLocator(widget, parentLocator);
        };

        assign();

        // Beim Aktualisieren des aktuellen Wertes wird das valueTemplate herangezogen, um diesen zu rendern
        // wenn da drin Elemente sind, müssen diese neu mit Locator-Attributen versehen werden
        widget.bind('cascade', assign);
        widget.value = ((value) => {
            return function () {
                var result = value.apply(widget, arguments);
                assign();
                return result;
            };
        })(widget.value);
    }

    public static bindWidgetLocators(widget, parentLocator, itemLocatorStrategy?) {
        if (Utilities.isNullOrUndefined(parentLocator)) {
            return;
        }

        KendoDropDownHelper.bindWidgetValueLocator(widget, parentLocator);
        KendoDropDownHelper.bindWidgetPopupLocators(widget, parentLocator, itemLocatorStrategy);
    }

    public static bindWidgetIsBusy(widget, busyObs) {
        KendoDropDownHelper.applyBindings(widget.popup.element,
            {
                loader: busyObs
            });
    }
    /**
     * 
     * @param items - Liste der Elemente, die im Dropdown-Menü angezeigt werden sollen
     * @param control - Das Parent-Element, bei dem die Dropdown-Liste gerendert werden soll
     * @param widget - Kendo Dropdown
     * @returns
     */
    public static resetDropDownPopupPosition(items: string[], control: JQuery<HTMLElement>, widget: kendo.ui.WidgetWithPopup, toTopOffset: number = 6, toBotomOffset: number = 1) {
        if (Utilities.isNullOrUndefined(widget) || Utilities.isNullOrUndefined(widget.popup))
            return;

        // wird durch IE erfordert, da das Popup im IE sonst verrutscht ist (Höhenberechnung funktioniert nicht)
        (widget.options as any).height = 'auto';
        // Überschreiben und somit deaktivieren
        widget.popup._position = () => {

        };

        //k-list-container - DropDown Control
        const wrapper: JQuery<HTMLElement> = widget.popup.element;
        const animationContainer = widget.popup.wrapper;

        // Maximale Breite bestimmen
        let wrapperWidth = 0;
        items.forEach((value) => {
            if (!value)
                return;
            const width = KendoDropDownHelper.getWidthByValue(wrapper, value);
            if (width > wrapperWidth)
                wrapperWidth = width;
        });

        // Innere Paddings/Borders des k-list-scroller und der Items etc. müssen addiert werden
        wrapperWidth = wrapperWidth + KendoDropDownHelper.getAdditionalWidth(wrapper);

        if (!Utilities.isNullOrUndefined(wrapper)) {
            const offset = 50;
            const listScroller = wrapper.find('.k-list-scroller');
            const scrollerHTMLElement = listScroller[0];

            // Wenn Scrollbar angezeigt wird: Breite erhöhen
            if (!Utilities.isNullOrUndefined(scrollerHTMLElement)) {
                const scrollbarWidth = scrollerHTMLElement.offsetWidth - scrollerHTMLElement.clientWidth;

                if (scrollbarWidth > 0) {
                    wrapperWidth = wrapperWidth + scrollbarWidth + parseFloat(wrapper.css('paddingRight')) || 0;
                }
            }

            const margins = KendoDropDownHelper.calculateMarginsOfControl(control, offset);
            wrapperWidth = KendoDropDownHelper.calculateWidth(
                margins,
                wrapperWidth,
                control.find('.k-widget').outerWidth(),
                // Paddings des Wrappers müssen abgezogen werden, da dieser sonst zu breit wird
                KendoDropDownHelper.getAdditionalWidthOfElement(wrapper)
            );

            // Maximale Höhe, statt Höhe setzen, da das das Dropdown
            // bei wenigen Elementen sonst zu hoch ist
            let maxHeight: number;
            wrapper.css('height', '');
            if (listScroller.length > 0) {
                listScroller.css('height', '');

                maxHeight = KendoDropDownHelper.calculateMaxHeight(margins, offset);
                listScroller.css('max-height', maxHeight + 'px');
            }

            // Höhe des Wrappers nach neuer Breite auslesen
            wrapper.width(wrapperWidth);
            const wrapperHeight = wrapper.height();

            if (Utilities.isNullOrUndefined(animationContainer))
                return;

            animationContainer.height(wrapperHeight);
            //Wenn sich die Breite der Liste der Elemente geändert hat, ist es auch notwendig, die Breite des AnimationContainer zu ändern
            animationContainer.width(wrapperWidth + KendoDropDownHelper.getAdditionalWidthOfElement(wrapper));

            // Neu positionieren
            var listScrollerHeight = listScroller.height();
            var elementHeight = listScrollerHeight > wrapperHeight ? listScrollerHeight : wrapperHeight;

            //Hier können wir direkt die Breite des Wrappers übergeben
            const newOffsets =
                KendoDropDownHelper.calculateNewMargins(margins, elementHeight, wrapperWidth, 6, 0, toTopOffset, toBotomOffset);

            animationContainer.css('left', newOffsets.offsetLeft);
            animationContainer.css('top', newOffsets.offsetTop);
        }
    }

    /**
     * Gibt die zusätzliche Breite der Liste zurück (Border, Paddings)
     * @param list
     */
    private static getAdditionalWidth(list: JQuery): number {
        const listScroller = list.find('.k-list-scroller');
        const item = list.find('.k-item');

        const listScrollerPaddings = KendoDropDownHelper.getAdditionalWidthOfElement(listScroller);
        //Wenn die Dropdown-Liste keine Elemente enthält, z.B. bei Multiselectdropdownlist,
        //dann gibt die Methode getAdditionalWidthOfElement NaN zurück.
        //Bei einem Dropdown, das sich in der rechten Spalte befand,
        //führte dies zu einer falschen Positionierung der Dropdown-Liste. 
        const itemPaddings = item.length > 0 ? KendoDropDownHelper.getAdditionalWidthOfElement(item) : 0;

        return listScrollerPaddings + itemPaddings;
    }

    /**
     * Gibt die zusätzliche Breite eines Elements zurück (Border, Paddings)
     * @param element
     */
    private static getAdditionalWidthOfElement(element: JQuery): number {
        return parseFloat(element.css('paddingLeft')) + parseFloat(element.css('paddingRight'))
            + parseFloat(element.css('borderLeftWidth')) + parseFloat(element.css('borderRightWidth'));
    }

    /**
    * Festlegung der Breite eines Dropdown-Menüelements durch Zeichnen dieses Elements im Canvas
    */
    private static getWidthByValue(popup: JQuery, value: any) {
        if (!value)
            return 0;
        if (value instanceof jQuery)
            value = (value as JQuery<HTMLElement>).text();
        else if (value instanceof HTMLElement)
            value = value.textContent;

        const fontFamily = popup.css('font-family');
        const fontSize = popup.css('font-size');

        let canvas = KendoDropDownHelper.canvas || (KendoDropDownHelper.canvas = document.createElement("canvas"));
        let context = canvas.getContext("2d");
        context.font = `${fontSize} ${fontFamily}`;
        let metrics = context.measureText(value);
        return metrics.width;
    }

    public static resetTreeViewDropDownPosition(control: JQuery<HTMLElement>, popup: JQuery<HTMLElement>, tree: JQuery<HTMLElement>) {
        const offset = 50;
        const margins = KendoDropDownHelper.calculateMarginsOfControl(control, offset);

        // Tree verkleinern (wenn möglich)
        popup.width('auto');

        const popupWidth = KendoDropDownHelper.calculateWidth(margins, tree.outerWidth(), control.outerWidth() - 2, 0);
        popup.width(popupWidth);

        const maxHeight = KendoDropDownHelper.calculateMaxHeight(margins, offset);
        popup.css('max-height', maxHeight + 'px');

        const newOffsets =
            KendoDropDownHelper.calculateNewMargins(margins, popup.outerHeight(), popupWidth, 2, 0, 0, 1);

        popup.css('height', 'auto');

        popup.css('left', newOffsets.offsetLeft);
        popup.css('top', newOffsets.offsetTop);
    }

    public static calculateMarginsOfControl(control: JQuery<HTMLElement>, offset: number = 0): Margins {
        const windowWidth = $(window).width();
        const windowHeight = $(window).height();
        const controlOffsetLeft = control.offset().left;
        const controlOffsetTop = control.offset().top;
        const controlWidth = control.width();
        const controlHeight = control.height();

        // Abstände nach außen berechnen
        const spaceToLeft = controlOffsetLeft + controlWidth - offset;
        const spaceToRight = windowWidth - controlOffsetLeft - offset;
        const spaceToTop = controlOffsetTop - offset;
        const spaceToBottom = windowHeight - controlOffsetTop - offset;

        return {
            width: controlWidth,
            height: controlHeight,
            offsetLeft: controlOffsetLeft,
            offsetTop: controlOffsetTop,
            spaceLeft: spaceToLeft,
            spaceTop: spaceToTop,
            spaceBottom: spaceToBottom,
            spaceRight: spaceToRight
        }
    }

    public static calculateWidth(margins: Margins, maxWidth: number = 0, minWidth: number = 0, paddings: number = 0): number {
        let width = 0;

        if (margins.spaceLeft <= margins.spaceRight && maxWidth > margins.spaceRight)
            width = margins.spaceRight;
        else if (margins.spaceLeft > margins.spaceRight && maxWidth > margins.spaceLeft)
            width = margins.spaceLeft;
        else
            width = maxWidth;

        if (width < minWidth)
            return minWidth - paddings;

        return width;
    }

    public static calculateMaxHeight(margins: Margins, fixedOffset: number) {
        return ((margins.spaceBottom > margins.spaceTop) ? margins.spaceBottom : margins.spaceTop) - fixedOffset;
    }

    public static calculateNewMargins(margins: Margins, elementHeight: number, elementWidth: number, fixedLeftOffset: number, fixedRightOffset: number, fixedTopOffset: number = 0, fixedBottomOffset: number = 0): Margins {
        let newTopOffset = 0;
        let newLeftOffset = 0;

        if (margins.spaceLeft <= margins.spaceRight)
            newLeftOffset = margins.offsetLeft - fixedRightOffset;
        else
            newLeftOffset = margins.offsetLeft + margins.width - elementWidth - fixedLeftOffset;

        if (margins.spaceBottom <= elementHeight)
            newTopOffset = margins.offsetTop - elementHeight - fixedTopOffset;
        else
            newTopOffset = margins.offsetTop + margins.height - fixedBottomOffset;

        return {
            offsetLeft: newLeftOffset,
            offsetTop: newTopOffset
        };
    }

    public static createComputedTooltip(
        controlTooltip: KnockoutObservable<string> | string, 
        dataObs: KnockoutObservableArray<any> | KnockoutComputed<any[]>, 
        valuesObs: KnockoutObservableArray<any> | KnockoutObservable<any>, 
        dataTextField: string, dataValueField: string, registerCallback: (sub: KnockoutSubscription) => void)
        : KnockoutObservable<string> {
        let controlToolTipInit = ko.isObservable(controlTooltip) ? controlTooltip() : controlTooltip;
        let obs = ko.observable(controlToolTipInit);
        
        const updateValue = () => {
            let controlToolTipResolved= ko.isObservable(controlTooltip) ? controlTooltip() : controlTooltip;
            let values: Array<any> = ko.isObservable(valuesObs) ? valuesObs() : [];
            const data: Array<any> = ko.isObservable(dataObs) ? dataObs() : [];

            if (values == null)
                values = [];
            if (!Array.isArray(values))
                values = [values];

            let selectedTexts = [];
            for (let x = 0; x < values.length; x++) {
                let currentValue = values[x];

                if (!Utilities.isNullOrUndefined(currentValue[dataTextField]))
                    selectedTexts.push(currentValue[dataTextField]);
                else {
                    let match = data.filter((item) => {
                        return item[dataValueField] == currentValue;
                    });
                    if (match.length > 0)
                        selectedTexts.push(match[0][dataTextField]);
                }
            }

            let stringParts = [];

            if (!Utilities.isNullOrUndefined(controlToolTipResolved) && !Utilities.isBlank(controlToolTipResolved))
                stringParts.push(controlToolTipResolved);

            if (selectedTexts.length > 0)
                stringParts.push(selectedTexts.join('\n'));

            obs(stringParts.join('\n\n'));
        };

        updateValue();

        if (!Utilities.isNullOrUndefined(valuesObs) && Utilities.isFunction(valuesObs.subscribe))
            registerCallback(valuesObs.subscribe(() => updateValue(), null, null));

        if (!Utilities.isNullOrUndefined(dataObs) && Utilities.isFunction(dataObs.subscribe))
            registerCallback(dataObs.subscribe(() => updateValue()));
       
        if (!Utilities.isNullOrUndefined(controlTooltip) && Utilities.isFunction((controlTooltip as KnockoutObservable<string>).subscribe))
            registerCallback((controlTooltip as KnockoutObservable<string>).subscribe(() => updateValue()));

        return obs;
    }

    private static applyBindings(element, bindings) {
        for (let binding in bindings) {
            if (!(binding in ko.bindingHandlers)) {
                throw 'Das Binding "' + binding + '" ist unbekannt oder wurde noch nicht registriert.';
            }
        }

        if (element[0] == null) {
            throw 'Bindings können nicht angewendet werden. Element ist null.';
        }

        ko.applyBindingsToNode(element[0], $.extend({ uiElement: self }, bindings));
    };

    private static defaultItemLocatorStrategy(item, idx, element) {
        const name = item.name;
        const objectIdentValue = item.objectIdentValue;

        if (!Utilities.isNullOrUndefined(name) && Utilities.isBlank(name))
            return KendoDropDownHelper.emptyItemLocator;

        if (!Utilities.isNullOrUndefined(objectIdentValue) && Utilities.isBlank(objectIdentValue))
            return KendoDropDownHelper.emptyItemLocator;

        return idx;
    }

    static readonly emptyItemLocator: string = '__empty__';
}

interface Margins {
    width?: number;
    height?: number;
    offsetLeft: number;
    offsetTop: number;
    spaceLeft?: number;
    spaceTop?: number;
    spaceRight?: number;
    spaceBottom?: number;
}